/**
 * Settings for the 🪝 wick-rejection candle marker (utils/wickRejection.ts):
 * emoji, wick % of range, wick in ATRs, confirmation bars, level-only,
 * broken hooks (small & faded, or hidden).
 * Visibility lives in levelsStore (`wickRejection`) with the other
 * candle-marker toggles, so Hide All and Clean (Signal markers) cover it.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { clampWickSettings, DEFAULT_WICK_SETTINGS, WickSettings } from '../utils/wickRejection';

interface WickRejectionSettingsState extends WickSettings {
  /** Merge and clamp; junk fields fall back to the defaults. */
  setSettings: (patch: Partial<WickSettings>) => void;
  reset: () => void;
}

const pick = (s: WickSettings): WickSettings => ({
  emoji: s.emoji,
  minWickPct: s.minWickPct,
  minWickAtr: s.minWickAtr,
  confirmBars: s.confirmBars,
  requireLevel: s.requireLevel,
  brokenHooks: s.brokenHooks,
});

export const useWickRejectionSettingsStore = create<WickRejectionSettingsState>()(
  persist(
    (set, get) => ({
      ...DEFAULT_WICK_SETTINGS,
      setSettings: (patch) => set(clampWickSettings({ ...pick(get()), ...patch })),
      reset: () => set({ ...DEFAULT_WICK_SETTINGS }),
    }),
    {
      name: 'wick-rejection-settings',
      partialize: (s) => pick(s),
      // Missing or junk values (older or hand-edited blobs) fall back to the defaults,
      // so a blob from before brokenHooks existed shows broken hooks small & faded.
      merge: (persisted, current) => ({ ...current, ...clampWickSettings(persisted as Partial<WickSettings>) }),
    },
  ),
);
